import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Check, X, ShoppingBag, RefreshCw, Phone, Package, User, Calendar, Share2, Camera, Download, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatMoeda = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

const formatDateBr = (ds) => {
  if (!ds) return "";
  const [y, m, d] = ds.split("-");
  return `${d}/${m}/${y}`;
};

export default function PedidosLojaPage() {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState(null);
  const [copiado, setCopiado] = useState(""); // "link" | "msg"
  const fotoInputRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/loja/pedidos-pendentes`);
      setPedidos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Erro ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const aprovar = async (id) => {
    setProcessando(id);
    try {
      await axios.put(`${API}/promissorias/${id}/aprovar`);
      toast.success("Pedido aprovado! Agora envie o WhatsApp para o cliente.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aprovar");
    } finally {
      setProcessando(null);
    }
  };

  const rejeitar = async (id) => {
    if (!window.confirm("Tem certeza que deseja cancelar este pedido?\n\nO pedido será cancelado e não aparecerá mais na lista.")) return;
    setProcessando(id);
    try {
      await axios.post(`${API}/promissorias/${id}/cancelar`);
      toast.success("Pedido cancelado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cancelar");
    } finally {
      setProcessando(null);
    }
  };

  const atualizarData = async (id, campo, valor) => {
    if (!valor) return;
    try {
      await axios.patch(`${API}/loja/pedidos/${id}/datas`, { [campo]: valor });
      setPedidos(prev => prev.map(p => p.id === id ? { ...p, [campo]: valor } : p));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar data");
    }
  };

  const enviarWhatsApp = async (p) => {
    const telefone = (p.cliente_telefone || "").replace(/\D/g, "");
    const vencFmt = formatDateBr(p.data_vencimento) || "-";
    const linkPublico = p.token_publico
      ? `${BACKEND_URL}/api/promissorias/${p.id}/pdf-publico?token=${p.token_publico}`
      : `${BACKEND_URL}/api/promissorias/${p.id}/pdf`;
    const texto = `Olá ${p.cliente_nome}! Parabéns pela sua compra!\n\n📋 *Pedido:* ${p.numero_pedido}\n💰 *Valor Total:* R$ ${Number(p.valor_total || 0).toFixed(2)}\n📅 *Vencimento:* ${vencFmt}\n\n📎 *Acesse sua nota promissória:*\n${linkPublico}\n\n*Adiantamentos via PIX:*\n*Bradesco - DAL COTONE MS*\n*PIX CNPJ:* 48578093000162`;
    const numWhatsApp = telefone.startsWith("595") ? telefone : `55${telefone}`;
    const url = telefone
      ? `https://wa.me/${numWhatsApp}?text=${encodeURIComponent(texto)}`
      : `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, "_blank");
    // Marcar como WhatsApp enviado (avança o fluxo)
    try {
      await axios.put(`${API}/loja/pedidos/${p.id}/marcar-whatsapp-enviado`);
      setPedidos(prev => prev.map(x => x.id === p.id ? { ...x, loja_fluxo: "aguardando_entrega" } : x));
      toast.success("WhatsApp aberto. Agora faça a entrega e anexe a foto.");
    } catch (err) {
      // Se já estava aguardando_entrega, ignora
    }
  };

  const triggerFoto = (id) => {
    const input = fotoInputRefs.current[id];
    if (input) { input.value = ""; input.click(); }
  };

  const enviarFotoEntrega = async (id, file) => {
    if (!file) return;
    setProcessando(id);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/promissorias/${id}/foto`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Venda entregue! Foto registrada.");
      // Remove do estado local
      setPedidos(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setProcessando(null);
    }
  };

  const copiarLinkApp = () => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const uid = u?.id;
    const nomeLoja = u?.nome_loja || "minha loja";
    const url = uid ? `${window.location.origin}/api/loja/share/${uid}` : `${window.location.origin}/loja`;
    const msg = `✨ *Baixe o App ${nomeLoja}* ✨\n\nFaça seus pedidos direto pelo seu celular, acompanhe seu cashback e receba ofertas exclusivas.\n\n📲 *Instalar o App:*\n${url}\n\n👉 *Como instalar:*\n1️⃣ Abra o link no Chrome (Android) ou Safari (iPhone)\n2️⃣ Toque em "Adicionar à tela de início" / "Instalar app"\n3️⃣ Pronto! Seu app ficará no menu do celular.\n\nQualquer dúvida estamos à disposição 💖`;
    navigator.clipboard.writeText(msg);
    setCopiado("msg");
    toast.success("Mensagem de download copiada! Cole no WhatsApp.");
    setTimeout(() => setCopiado(""), 2500);
  };

  const enviarLinkWhatsApp = () => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const uid = u?.id;
    const nomeLoja = u?.nome_loja || "minha loja";
    const url = uid ? `${window.location.origin}/api/loja/share/${uid}` : `${window.location.origin}/loja`;
    const msg = `✨ *Baixe o App ${nomeLoja}* ✨\n\nFaça seus pedidos direto pelo seu celular, acompanhe seu cashback e receba ofertas exclusivas.\n\n📲 *Instalar o App:*\n${url}\n\n👉 *Como instalar:*\n1️⃣ Abra o link no Chrome (Android) ou Safari (iPhone)\n2️⃣ Toque em "Adicionar à tela de início" / "Instalar app"\n3️⃣ Pronto! Seu app ficará no menu do celular.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const fmtDateTime = (s) => {
    if (!s) return "";
    try {
      return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Campo_Grande", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return s; }
  };

  const tempoDecorrido = (iso) => {
    if (!iso) return "";
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const min = Math.floor(diff / 60000);
    if (min < 1) return "agora";
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    return `há ${d}d`;
  };

  // Re-render a cada 60s para atualizar os timers
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(iv);
  }, []);

  const getEtapa = (p) => {
    if (p.status === "aguardando_aprovacao") return "aprovar";
    if (p.loja_fluxo === "aguardando_whatsapp") return "whatsapp";
    if (p.loja_fluxo === "aguardando_entrega") return "entregar";
    return "aprovar";
  };

  const badgeEtapa = {
    aprovar: { label: "AGUARDANDO APROVAÇÃO", cls: "bg-amber-200 text-amber-900" },
    whatsapp: { label: "ENVIAR WHATSAPP AO CLIENTE", cls: "bg-green-200 text-green-900 animate-pulse" },
    entregar: { label: "AGUARDANDO ENTREGA + FOTO", cls: "bg-red-200 text-red-900 animate-pulse" },
  };

  const borderEtapa = {
    aprovar: "border-l-amber-500 bg-amber-50/30",
    whatsapp: "border-l-green-500 bg-green-50/30",
    entregar: "border-l-red-500 bg-red-50/30",
  };

  return (
    <div className="space-y-4" data-testid="pedidos-loja-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-6 w-6 text-emerald-600" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Work Sans" }}>Pedidos da Loja Virtual</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={copiarLinkApp} className="gap-1 text-pink-700 border-pink-300 hover:bg-pink-50" data-testid="btn-copiar-link-app" title="Copia a mensagem pronta para enviar ao cliente com instruções de download">
            <Download className="h-4 w-4" /> {copiado === "msg" ? "Copiado!" : "Copiar Link do App"}
          </Button>
          <Button variant="outline" size="sm" onClick={enviarLinkWhatsApp} className="gap-1 text-green-700 border-green-300 hover:bg-green-50" data-testid="btn-enviar-link-whatsapp" title="Abre o WhatsApp com a mensagem pronta">
            <Share2 className="h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid="btn-recarregar-pedidos">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500">Pedidos da Loja Virtual em andamento. Fluxo: <strong>Aprovar</strong> → <strong>Enviar WhatsApp</strong> → <strong>Venda Entregue (foto)</strong>. Só saem desta lista quando a foto da entrega for enviada.</p>

      {loading && pedidos.length === 0 && (
        <p className="text-center text-gray-400 py-16">Carregando...</p>
      )}

      {!loading && pedidos.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Nenhum pedido em andamento</p>
            <p className="text-xs text-gray-400 mt-1">Compartilhe o link da loja com seus clientes para começarem a pedir.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {pedidos.map((p) => {
          const etapa = getEtapa(p);
          const badge = badgeEtapa[etapa];
          const borderCls = borderEtapa[etapa];
          return (
            <Card key={p.id} className={`border-l-4 ${borderCls}`} data-testid={`pedido-${p.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" /> {p.cliente_nome}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {p.cliente_telefone || "-"}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDateTime(p.created_at)}</span>
                      <span>Pedido #{p.numero_pedido}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-700">{formatMoeda(p.valor_total)}</p>
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.cls}`} data-testid={`etapa-badge-${p.id}`}>
                      {badge.label}
                    </span>
                    {etapa === 'aprovar' && (
                      <p className="text-[10px] text-gray-500 mt-0.5" data-testid={`timer-${p.id}`}>Criado {tempoDecorrido(p.created_at)}</p>
                    )}
                    {etapa === 'whatsapp' && p.aprovado_em && (
                      <p className="text-[10px] text-green-700 font-medium mt-0.5" data-testid={`timer-${p.id}`}>Aprovado {tempoDecorrido(p.aprovado_em)}</p>
                    )}
                    {etapa === 'entregar' && p.whatsapp_enviado_em && (
                      <p className="text-[10px] text-red-700 font-medium mt-0.5" data-testid={`timer-${p.id}`}>WhatsApp {tempoDecorrido(p.whatsapp_enviado_em)}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* Datas editáveis */}
                <div className="grid grid-cols-2 gap-2 bg-white rounded-lg border p-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">Data Venda</label>
                    <input
                      type="date"
                      defaultValue={(p.data_compra || "").slice(0, 10)}
                      onBlur={(e) => atualizarData(p.id, "data_compra", e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid={`data-venda-${p.id}`}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">Data Vencimento</label>
                    <input
                      type="date"
                      defaultValue={(p.data_vencimento || "").slice(0, 10)}
                      onBlur={(e) => atualizarData(p.id, "data_vencimento", e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      data-testid={`data-venc-${p.id}`}
                    />
                  </div>
                </div>

                {/* Itens */}
                <div className="bg-white rounded-lg border p-3">
                  <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Itens ({(p.itens || []).length})</p>
                  <div className="space-y-1">
                    {(p.itens || []).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="flex-1">
                          <span className="text-gray-400 mr-2">{it.produto_codigo}</span>
                          <span className="font-medium">{it.produto_nome}</span>
                        </span>
                        <span className="text-gray-500 w-16 text-right">{it.quantidade}x {formatMoeda(it.preco_unitario)}</span>
                        <span className="font-bold w-20 text-right">{formatMoeda(it.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {p.descricao_compra && p.descricao_compra !== "Pedido via Loja Virtual" && (
                  <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 italic">📝 {p.descricao_compra}</p>
                )}

                {/* Aviso Nota Aberta — bloqueia aprovação */}
                {etapa === 'aprovar' && p.nota_aberta_info && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-xs" data-testid={`aviso-nota-aberta-${p.id}`}>
                    <p className="font-bold text-red-800">⚠️ Cliente com Nota em Aberto</p>
                    <p className="text-red-700 mt-0.5">Nota #{p.nota_aberta_info.nota_numero} · Restante <strong>{formatMoeda(p.nota_aberta_info.restante)}</strong></p>
                    <p className="text-[10px] text-red-600 mt-1">A aprovação deste pedido será liberada automaticamente após a quitação da nota em aberto.</p>
                  </div>
                )}

                {/* Input hidden p/ câmera */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  ref={(el) => { fotoInputRefs.current[p.id] = el; }}
                  onChange={(e) => enviarFotoEntrega(p.id, e.target.files?.[0])}
                  data-testid={`foto-entrega-input-${p.id}`}
                />

                {/* Botões conforme etapa */}
                <div className="flex gap-2 justify-end flex-wrap">
                  {etapa === "aprovar" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/nova-promissoria?editar=${p.id}`)} className="text-amber-700 border-amber-300 hover:bg-amber-50 gap-1" data-testid={`btn-alterar-pedido-${p.id}`}>
                        <RotateCcw className="h-4 w-4" /> Alterar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => rejeitar(p.id)} disabled={processando === p.id} className="text-orange-600 border-orange-300 hover:bg-orange-50 gap-1" data-testid={`btn-rejeitar-${p.id}`}>
                        <X className="h-4 w-4" /> Cancelar
                      </Button>
                      <Button size="sm" onClick={() => aprovar(p.id)} disabled={processando === p.id || !!p.nota_aberta_info} className="bg-emerald-600 hover:bg-emerald-700 gap-1 disabled:opacity-60 disabled:cursor-not-allowed" data-testid={`btn-aprovar-${p.id}`} title={p.nota_aberta_info ? 'Bloqueado: cliente tem nota em aberto' : ''}>
                        <Check className="h-4 w-4" /> {processando === p.id ? "..." : (p.nota_aberta_info ? 'Aguarda quitação' : 'Aprovar')}
                      </Button>
                    </>
                  )}
                  {etapa === "whatsapp" && (
                    <Button size="sm" onClick={() => enviarWhatsApp(p)} className="bg-green-600 hover:bg-green-700 gap-1 animate-pulse" data-testid={`btn-whatsapp-${p.id}`}>
                      <Share2 className="h-4 w-4" /> Enviar WhatsApp
                    </Button>
                  )}
                  {etapa === "entregar" && (
                    <Button size="sm" onClick={() => triggerFoto(p.id)} disabled={processando === p.id} className="bg-red-600 hover:bg-red-700 gap-1 animate-pulse" data-testid={`btn-venda-entregue-${p.id}`}>
                      <Camera className="h-4 w-4" /> {processando === p.id ? "Enviando..." : "Venda Entregue"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
